/* ============================================
   PartnerLayout — el panel de marca blanca del partner

   Sin marca 1Conecta: la cabecera lleva el nombre de SU empresa. El menu se
   agrupa como su propio CRM (ver el comentario de los grupos, abajo).
   ============================================ */

import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useApi } from '@/hooks/useApi'
import { useState, useEffect } from 'react'
import ToastContainer from '@/components/layout/Toast'
import ChatNotificationProvider from '@/components/layout/ChatNotificationProvider'
import { PortalClienteProvider } from '@/hooks/usePortalCliente'
import AccountSwitcher from '@/components/auth/AccountSwitcher'
import UserMenu from '@/components/layout/UserMenu'

/* El panel del partner se agrupa como SU CRM, no como nuestro portal.
   
   Antes eran dos zonas, "Mi cuenta" y "Mi negocio", y el orden decia lo
   contrario de lo que es: su negocio es todo el panel, y nuestra relacion con
   el es un rincon. Ahora el reparto copia al sidebar del ERP —vende (CRM),
   entrega (Operaciones), consulta (Clientes)— y lo nuestro vive al final, en
   "Afiliados": el catalogo que puede revender y lo que le cobramos.
   
   "Afiliados" junta las dos cosas porque las dos nacen de la misma relacion, y
   el nombre resuelve solo la ambiguedad que habia antes: "Cotizaciones" dentro
   de CRM son las que el emite, y "Mis cotizaciones" dentro de Afiliados son las
   que le pasamos nosotros.
   
   Mismas clases (nav-group / nav-sub-item) que el sidebar del ERP. */
const ICON_CRM = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
  </svg>
)
const ICON_OPERACIONES = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/><line x1="9" y1="7" x2="15" y2="7"/>
  </svg>
)
const ICON_CLIENTES = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const ICON_AFILIADOS = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 17 9.5 15.5" /><path d="m14 14-1.5-1.5" />
    <path d="M8.5 20.5 3 15a2.12 2.12 0 0 1 0-3l4-4" />
    <path d="m13 3 4 4" /><path d="M7 8h4l2.5 2.5" />
    <path d="M17 7h4v6.5a2.12 2.12 0 0 1-3.6 1.5L16 13.5" />
    <path d="M12.5 20.5a2.12 2.12 0 0 0 3 0l1-1" />
  </svg>
)

export default function PartnerLayout() {
  const { signOut, clienteId, user, role } = useAuth()
  const api = useApi()
  const navigate = useNavigate()
  const location = useLocation()
  const [empresaNombre, setEmpresaNombre] = useState('Partner')
  // Quien esta usando el panel. Sale del CONTACTO (via /portal/perfil) y no de
  // `usuarios.nombre`, que es lo que lee el pie del ERP: el partner edita su
  // nombre en Mi perfil, y eso escribe el contacto. Leyendo la otra columna el
  // pie seguiria mostrando el valor viejo despues de guardar.
  const [personaNombre, setPersonaNombre] = useState<string | null>(null)
  // El logo de la empresa, si lo subio. Sin el, la marca del panel es la
  // inicial en un cuadrado de color, que es lo que habia hasta ahora.
  const [empresaLogo, setEmpresaLogo] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  // Hosting no es universal: solo en el menu si el partner tiene alguno.
  const [tieneHosting, setTieneHosting] = useState(false)

  const navGroups = [
    {
      label: 'CRM',
      basePath: '/partner/pipeline',
      icon: ICON_CRM,
      children: [
        { to: '/partner/prospeccion', label: 'Prospección' },
        { to: '/partner/pipeline', label: 'Pipeline' },
        // Las de su cartera. Las que le enviamos a el viven en Afiliados, con
        // el posesivo: "Mis cotizaciones". Misma palabra, dos direcciones.
        { to: '/partner/cartera/cotizaciones', label: 'Cotizaciones' },
        // Servicios vuelve al menu, ahora que hay algo detras: su catalogo
        // propio. Estaba enlazado a una pantalla vacia y por eso se saco.
        { to: '/partner/cartera/servicios', label: 'Servicios' },
        { to: '/partner/tareas', label: 'Tareas' },
        // Conversaciones vuelve al menu: la pantalla dejo de ser un cartel de
        // "proximamente" y ahora dice si su WhatsApp esta conectado y que le
        // falta para conectarlo.
        { to: '/partner/conversaciones', label: 'Conversaciones' },
      ],
    },
    {
      label: 'Operaciones',
      basePath: '/partner/proyectos',
      icon: ICON_OPERACIONES,
      children: [
        { to: '/partner/proyectos', label: 'Proyectos' },
        // El de su cartera. El suyo propio esta en Afiliados, como "Mi
        // hosting": lo que gestiona y lo que recibe no se mezclan.
        { to: '/partner/cartera/hosting', label: 'Hosting' },
      ],
    },
    {
      label: 'Clientes',
      basePath: '/partner/empresas',
      icon: ICON_CLIENTES,
      children: [
        { to: '/partner/empresas', label: 'Empresas' },
        { to: '/partner/contactos', label: 'Contactos' },
      ],
    },
    {
      label: 'Afiliados',
      basePath: '/partner/mi-cuenta',
      icon: ICON_AFILIADOS,
      children: [
        { to: '/partner/servicios', label: 'Catálogo' },
        { to: '/partner/mi-cuenta', label: 'Mi cuenta' },
        { to: '/partner/cotizaciones', label: 'Mis cotizaciones' },
        { to: '/partner/facturas', label: 'Mis facturas' },
        ...(tieneHosting ? [{ to: '/partner/hosting', label: 'Mi hosting' }] : []),
      ],
    },
  ]

  // Abre CRM y deja el resto plegado: es donde trabaja todos los dias. A
  // Afiliados entra cuando le llega un correo nuestro, dos veces al mes.
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(['/partner/pipeline'])
  )

  const toggleGroup = (basePath: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(basePath)) next.delete(basePath)
      else next.add(basePath)
      return next
    })
  }

  // Nombre de la empresa (cabecera) y de la persona (pie) en una sola ida:
  // /portal/perfil devuelve los dos, y resuelve la empresa por el token en vez
  // de por un id en la URL.
  useEffect(() => {
    if (!clienteId) return
    api.get<{ personal?: { nombre?: string }; empresa?: { nombre_empresa?: string; logo_url?: string | null } }>('/api/portal/perfil')
      .then(res => {
        if (res?.empresa?.nombre_empresa) setEmpresaNombre(res.empresa.nombre_empresa)
        if (res?.personal?.nombre) setPersonaNombre(res.personal.nombre)
        setEmpresaLogo(res?.empresa?.logo_url || null)
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  // ¿El partner tiene hosting propio? (decide el item de nav de Mi cuenta)
  useEffect(() => {
    api.get<unknown[]>('/api/hosting/mi-hosting')
      .then(d => setTieneHosting(Array.isArray(d) && d.length > 0))
      .catch(() => setTieneHosting(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = () => {
    signOut()
    navigate('/login')
  }

  return (
    <ChatNotificationProvider>
     <PortalClienteProvider>
      <div className={`partner-layout ${collapsed ? 'partner-layout--collapsed' : ''}`}>
        {/* Sidebar */}
        <aside className="partner-sidebar">
          <div className="partner-sidebar__header">
            <div className="partner-sidebar__brand">
              <div className={`partner-sidebar__avatar${empresaLogo ? ' partner-sidebar__avatar--logo' : ''}`}>
                {empresaLogo
                  ? <img src={empresaLogo} alt="" />
                  : empresaNombre.charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <span className="partner-sidebar__name">{empresaNombre}</span>
              )}
            </div>
            <button
              className="partner-sidebar__toggle"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {collapsed ? (
                  <polyline points="9 18 15 12 9 6" />
                ) : (
                  <polyline points="15 18 9 12 15 6" />
                )}
              </svg>
            </button>
          </div>

          {!collapsed && (
            <div style={{ padding: '10px 12px 0' }}>
              <AccountSwitcher variant="dark" />
            </div>
          )}

          <nav className="partner-sidebar__nav">
            {navGroups.map((group) => {
              const isOpen = openGroups.has(group.basePath)
              const childActive = group.children.some(ch =>
                location.pathname === ch.to || location.pathname.startsWith(ch.to + '/')
              )

              return (
                <div key={group.basePath} className={`nav-group${isOpen ? ' nav-group--open' : ''}`}>
                  <div
                    className={`nav-item nav-group-header${childActive && collapsed ? ' active' : ''}`}
                    onClick={() => {
                      navigate(group.basePath)
                      setOpenGroups(prev => new Set(prev).add(group.basePath))
                    }}
                    title={collapsed ? group.label : undefined}
                  >
                    {group.icon}
                    {!collapsed && (
                      <>
                        <span className="nav-label">{group.label}</span>
                        <span
                          className="nav-group-chevron-btn"
                          onClick={(e) => { e.stopPropagation(); toggleGroup(group.basePath) }}
                          title={isOpen ? 'Colapsar' : 'Expandir'}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            style={{ marginLeft: 'auto', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </>
                    )}
                  </div>

                  {!collapsed && (
                    <div
                      className="nav-group-children"
                      style={{ maxHeight: isOpen ? `${group.children.length * 40}px` : '0' }}
                    >
                      {group.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) => `nav-item nav-sub-item${isActive ? ' active' : ''}`}
                        >
                          <span className="nav-label">{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Configuracion salio del menu: vive en los tres puntitos del pie,
               al lado de Mi perfil. Se entra un punado de veces —conectar el
               WhatsApp, sumar a alguien al equipo— y ocupaba un lugar fijo
               compitiendo con el trabajo de todos los dias, el mismo criterio
               que puso Configuracion en el pie del ERP.

               Y son dos cosas distintas a proposito: Mi perfil es la persona
               (quien soy, como entro) y Configuracion es la cuenta (la empresa,
               el equipo, las integraciones). Estaban mezclados, asi que para
               cambiar el RUC habia que entrar a "mi perfil". */}
          </nav>

          {/* El pie dice QUIEN esta usando el panel, no solo como salir: era un
              boton de "Cerrar sesion" y nada mas, asi que un partner no tenia
              desde donde cambiar su contrasena ni corregir su telefono. Mismo
              componente que el pie del ERP; cambian los items que lleva. */}
          <div className="partner-sidebar__footer">
            <UserMenu
              nombre={personaNombre || user?.nombre}
              email={user?.email || ''}
              rol={role}
              fotoUrl={user?.foto_url}
              colapsado={collapsed}
              onCerrarSesion={handleLogout}
              enlaces={[
                { label: 'Mi perfil', to: '/partner/perfil', icono: 'perfil' },
                { label: 'Configuración', to: '/partner/ajustes', icono: 'ajustes' },
              ]}
              tema={false}
            />
          </div>
        </aside>

        {/* Main content */}
        <main className="partner-main">
          <Outlet />
        </main>

        <ToastContainer />
      </div>
     </PortalClienteProvider>
    </ChatNotificationProvider>
  )
}
